import { useEffect, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Icons } from './Icons';
import { useTheme } from '../lib/ThemeProvider';
import { FabSpeedDial, type TxKind } from './FabSpeedDial';
import { TransactionModal } from './transactions/TransactionModal';
import { useTxModal } from '../features/transactions/txModalStore';

export function Layout() {
  const { theme, toggle } = useTheme();
  const [newType, setNewType] = useState<TxKind | null>(null);
  const editing = useTxModal((s) => s.editing);
  const closeEdit = useTxModal((s) => s.close);

  const handleNewTransaction = (kind: TxKind) => setNewType(kind);

  // Skrót klawiszowy "n" = nowy wydatek (desktop power-user)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (target?.isContentEditable) return;
      if (e.key === 'n' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setNewType('expense');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="min-h-screen flex bg-slate-100 dark:bg-slate-900">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-60 shrink-0 sticky top-0 h-screen border-r border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 backdrop-blur"
        aria-label="Nawigacja boczna"
      >
        <div className="px-5 h-16 flex items-center gap-2.5 border-b border-slate-200 dark:border-slate-800">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
            <Icons.Heart size={20} className="text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-display font-bold text-slate-900 dark:text-white leading-tight">Rodzinka</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">Finance</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <SidebarItem to="/dashboard" icon={Icons.Dashboard} label="Pulpit" />
          <SidebarItem to="/analytics" icon={Icons.Analytics} label="Analityka" />
          <SidebarItem to="/goals" icon={Icons.Goals} label="Cele" />
          <SidebarItem to="/budgets" icon={Icons.ChartPie} label="Budżety" />
          <SidebarItem to="/myportfolio" icon={Icons.Profile} label="Mój profil" />
          <div className="pt-2 mt-2 border-t border-slate-200 dark:border-slate-800">
            <SidebarItem to="/settings" icon={Icons.Settings} label="Ustawienia" />
          </div>
        </nav>
        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={toggle}
            className="w-full px-3 py-2 rounded-lg text-sm flex items-center gap-2.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            aria-label="Przełącz motyw"
          >
            {theme === 'dark' ? <Icons.Sun size={16} /> : <Icons.Moon size={16} />}
            <span>{theme === 'dark' ? 'Jasny motyw' : 'Ciemny motyw'}</span>
          </button>
          <p className="text-[10px] text-slate-400 dark:text-slate-500 px-3 mt-2">
            Skrót: <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-500 font-mono text-[9px]">n</kbd> = nowy wydatek
          </p>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header (lg:hidden) */}
        <header className="lg:hidden sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800">
          <div className="max-w-screen-sm mx-auto px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
                <Icons.Heart size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <span className="font-display font-bold text-slate-900 dark:text-white">Rodzinka</span>
            </div>
            <button
              onClick={toggle}
              className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Przełącz motyw"
            >
              {theme === 'dark' ? <Icons.Sun size={18} /> : <Icons.Moon size={18} />}
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-12">
          <div className="max-w-screen-sm lg:max-w-5xl mx-auto px-4 lg:px-8 py-4 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom nav — TYLKO mobile/tablet (< lg). Desktop sidebar w Fazie 7. */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t border-slate-200 dark:border-slate-800 safe-area-bottom"
        aria-label="Nawigacja główna"
      >
        <div className="grid grid-cols-5 max-w-screen-sm mx-auto">
          <NavItem to="/dashboard" icon={Icons.Dashboard} label="Start" />
          <NavItem to="/analytics" icon={Icons.Analytics} label="Analityka" />
          <div className="flex items-center justify-center -mt-6">
            <FabSpeedDial onPick={handleNewTransaction} />
          </div>
          <NavItem to="/goals" icon={Icons.Goals} label="Cele" />
          <NavItem to="/myportfolio" icon={Icons.Profile} label="Profil" />
        </div>
      </nav>

      {/* Floating FAB na desktop (lg+) — bottom-right żeby nie tracić szybkości
          dodawania transakcji. Constraint usera: speed-dial zostaje na każdym
          breakpoint. Skrót `n` (Faza 7) — TODO. */}
      <div className="hidden lg:flex fixed bottom-6 right-6 z-40">
        <FabSpeedDial onPick={handleNewTransaction} />
      </div>

      {newType && (
        <TransactionModal type={newType} onClose={() => setNewType(null)} />
      )}
      {editing && (
        <TransactionModal type={editing.type} edit={editing} onClose={closeEdit} />
      )}
    </div>
  );
}

function NavItem({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof Icons.Dashboard;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 min-h-[3.5rem] transition-colors ${
          isActive ? 'text-sky-600 dark:text-sky-300' : 'text-slate-500 dark:text-slate-400'
        }`
      }
    >
      <Icon size={22} />
      <span className="text-[10px] font-medium">{label}</span>
    </NavLink>
  );
}

function SidebarItem({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof Icons.Dashboard;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
          isActive
            ? 'bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300'
            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );
}
